import type { UserResponse } from '@descope/core-js-sdk';
import type { FlowJWTResponse } from '@descope/web-component';
import { useState, useEffect } from 'react';
import { Descope } from '@descope/react-sdk';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { trackEvent } from '@/lib/analytics';
import { isTelemetryEnabled } from '@/lib/env';
import { useThemeContext } from '@/hooks/theme/useThemeContext';
import { getDisplayName } from '@/lib/utils';
import type { ThemePreferenceType } from '@/styles/types';
import { THEME_PREFERENCES } from '@/styles/constants';

/**
 * Infer the authentication method from Descope user object.
 * Checks OAuth providers first, then falls back to password/email.
 */
function getSignupMethod(user: UserResponse): string {
  // Check OAuth providers (google, github, microsoft, etc.)
  if (user.OAuth) {
    const oauthProvider = Object.keys(user.OAuth).find(
      provider => user.OAuth?.[provider]
    );
    if (oauthProvider) {
      return oauthProvider;
    }
  }

  // Fall back to password-based auth
  if (user.password) {
    return 'email';
  }

  return 'unknown';
}

const loadingMessages = [
  'Firing up the engines...',
  'Warming up the quantum processors...',
  'Calibrating the flux capacitor...',
  'Initializing the authentication matrix...',
  'Charging the login cannon...',
  'Summoning the digital spirits...',
  'Defragmenting the internet...',
  'Herding the security cats...',
  'Consulting the oracle database...',
  'Spinning up the hamster wheels... ',
];

/**
 * type ThemeOptions for descope SDK is light|dark|os
 * Returning 'os' for system theme applies the os theme to the Descope element but it throws this error:
 * LandingPage.tsx:75 Supported theme values are "light", "dark", or leave empty for using the OS theme which can be ignored
 *
 * @param themePreference
 * @returns 'light' | 'dark' | 'os'
 */
const getDescopeThemeByPreference = (
  themePreference: ThemePreferenceType
): 'light' | 'dark' | 'os' => {
  if (themePreference === THEME_PREFERENCES.SYSTEM) return 'os';
  return themePreference;
};

/**
 * Build the OAuth redirect URL, preserving the user's intended destination.
 *
 * When an unauthenticated user clicks a deep link (e.g. an alert notification
 * pointing at `/{orgId}/alerts/{alertKey}/occurrences`), the LandingPage
 * replaces the routes with the Descope flow. For password-based flows the
 * pathname survives because React Router re-renders the original URL on
 * auth-state change, but OAuth flows hand control to Descope which then
 * navigates the browser to `redirectUrl`. Pointing `redirectUrl` at the
 * current location (instead of just `origin`) keeps the deep link intact.
 *
 * Captured once at first render via `useState`, so a partial OAuth navigation
 * after re-render doesn't see a mutated pathname.
 */
function buildPostLoginRedirectUrl(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const { origin, pathname, search, hash } = window.location;
  return `${origin}${pathname}${search}${hash}`;
}

export default function LandingPage() {
  const { value: themePreference } = useThemeContext();
  const [isDescopeLoaded, setIsDescopeLoaded] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [redirectUrl] = useState(buildPostLoginRedirectUrl);

  useEffect(() => {
    const timer = setTimeout(() => setIsDescopeLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isDescopeLoaded) {
      const interval = setInterval(() => {
        setMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [isDescopeLoaded]);

  const handleLoginSuccess = (e: CustomEvent<FlowJWTResponse>): void => {
    const response = e.detail;

    // Track first-time login for new users (signup)
    // Descope's firstSeen flag is the authoritative indicator of a new user
    // Note: We explicitly include user_id here since this fires before
    // AuthenticatedTelemetry sets the authenticated user for GTM auto-injection
    if (isTelemetryEnabled() && response.firstSeen && response.user) {
      trackEvent('first_time_login', {
        user_id: response.user.userId,
        user_name: getDisplayName(response.user),
        email: response.user.email,
        email_domain: response.user.email?.split('@')[1],
        signup_method: getSignupMethod(response.user),
      });
    }

    // Log login event to audit logs (non-blocking, fire-and-forget)
    apiClient.post('/account/login').catch(err => {
      console.warn('Failed to log login event:', err);
      // Don't prevent login if audit logging fails
    });
  };

  const descopeTheme = getDescopeThemeByPreference(themePreference);

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          {!isDescopeLoaded ? (
            <div className="bg-white rounded-lg shadow-sm border p-8">
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <p className="text-gray-600 text-center min-h-[1.5rem]">
                  {loadingMessages[messageIndex]}
                </p>
              </div>
            </div>
          ) : (
            <Descope
              flowId="sign-up-or-in"
              onSuccess={handleLoginSuccess}
              onError={e => console.error('Login error:', e)}
              redirectUrl={redirectUrl}
              theme={descopeTheme}
            />
          )}
        </div>
      </div>
    </div>
  );
}
